import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PaperPlaneTilt, 
  Microphone, 
  MicrophoneSlash, 
  Upload, 
  Chat, 
  AndroidLogo, 
  User, 
  CircleNotch, 
  CheckCircle, 
  XCircle, 
  SpeakerHigh, 
  SpeakerX, 
  Plus, 
  Trash, 
  PencilSimple, 
  BookOpen, 
  Database, 
  Wrench, 
  FolderPlus, 
  X, 
  Warning, 
  Key, 
  Gear, 
  Globe 
} from '@phosphor-icons/react';
import { toast } from 'react-hot-toast';
import { useBusinessType } from '../../context/BusinessContext';
import { getSupabaseClient } from '../../supabaseClient';
import { cn } from '../../lib/utils';
import { API_BASE_URL, endpoints } from '../../config/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const BrainChatInterface = ({ 
  isOpen, 
  onClose, 
  onResultsApproved,
  className = ""
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentOperation, setCurrentOperation] = useState(null);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState(null);

  // File upload state
  const [dragActive, setDragActive] = useState(false);

  // Analysis results
  const [analysisResults, setAnalysisResults] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [deniedOperations, setDeniedOperations] = useState([]);

  // Current settings data for showing diffs in edit operations
  const [currentSettingsData, setCurrentSettingsData] = useState({});

  // Audio playback
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);

  // Complex answer state
  const [editingSettings, setEditingSettings] = useState({});

  // Error handling and recovery state
  const [analysisErrors, setAnalysisErrors] = useState(0);
  const [lastErrorTime, setLastErrorTime] = useState(0);
  const [isCircuitOpen, setIsCircuitOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom only for new messages (not updates)
  const [lastMessageCount, setLastMessageCount] = useState(0);
  useEffect(() => {
    if (messages.length > lastMessageCount) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setLastMessageCount(messages.length);
    }
  }, [messages, lastMessageCount]);

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const lineHeight = 20; // Line height in pixels
    const minRows = 1;
    const maxRows = 12;
    const minHeight = minRows * lineHeight;

    // If there's no actual content (empty or just whitespace), set to minimum height
    if (!inputText.trim()) {
      textarea.style.height = `${minHeight}px`;
      textarea.style.overflowY = 'hidden';
      return;
    }

    // Reset height to get accurate scroll height for content
    textarea.style.height = 'auto';

    // Calculate required rows based on scroll height
    const scrollHeight = textarea.scrollHeight;
    const calculatedRows = Math.ceil(scrollHeight / lineHeight);

    // Apply constraints: minimum 1 row, maximum 5 rows
    const targetRows = Math.min(Math.max(calculatedRows, minRows), maxRows);
    const targetHeight = targetRows * lineHeight;

    // Set height
    textarea.style.height = `${targetHeight}px`;

    // Enable/disable scrolling based on whether we're at max rows
    if (calculatedRows > maxRows) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  // Set initial height on component mount
  useEffect(() => {
    if (textareaRef.current) {
      const lineHeight = 20;
      textareaRef.current.style.height = `${lineHeight}px`;
      textareaRef.current.style.overflowY = 'hidden';
    }
  }, []);

  // Adjust height when input text changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputText]);

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }

    return () => clearInterval(recordingTimerRef.current);
  }, [isRecording]);

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle text input
  const handleTextSubmit = async () => {
    if (!inputText.trim() || isProcessing) return;

    const userMessage = inputText.trim();
    setInputText('');

    // Add user message
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    // Process the message
    await processUserInput(userMessage, 'text');
  };

  // Start voice recording with optimized format selection
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try to use the most compatible format for the server with better fallbacks
      const supportedTypes = [
        'audio/webm', // Start with basic webm without codec specification
        'audio/webm;codecs=opus',
        'audio/mp4',
        'audio/ogg',
        'audio/wav'
      ];

      let selectedType = '';

      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          selectedType = type;
          break;
        }
      }

      // Final fallback - don't specify type, let browser choose
      if (!selectedType) {
        selectedType = 'audio/webm';
      }

      // Create MediaRecorder with fallback options
      let recorder;
      let actualMimeType = selectedType;

      try {
        // Try with specified codec first
        recorder = new MediaRecorder(stream, { 
          mimeType: selectedType,
          audioBitsPerSecond: 128000 // 128kbps for good quality/size balance
        });
      } catch (codecError) {
        try {
          // Try basic format without codec specification
          const basicType = selectedType.split(';')[0]; // Remove codec specification
          recorder = new MediaRecorder(stream, { 
            mimeType: basicType,
            audioBitsPerSecond: 128000
          });
          actualMimeType = basicType;
        } catch (basicError) {
          // Last resort - no codec specification at all
          recorder = new MediaRecorder(stream);
          actualMimeType = recorder.mimeType || 'audio/webm';
        }
      }
      const chunks = [];

      // Collect audio data
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      // Add error handling
      recorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        toast.error('Recording error occurred');
      };

      recorder.onstop = async () => {
        // If no chunks collected, this indicates a MediaRecorder issue
        if (chunks.length === 0) {
          console.error('No audio chunks collected during recording!');
          toast.error('Recording failed - no audio data captured. Please check microphone permissions and try again.');
          return;
        }

        // Use the actual MIME type from MediaRecorder or the type that was successfully created
        const mimeType = recorder.mimeType || actualMimeType;
        const extension = mimeType.includes('webm') ? 'webm' : 
                         mimeType.includes('mp4') ? 'mp4' : 
                         mimeType.includes('ogg') ? 'ogg' :
                         mimeType.includes('wav') ? 'wav' : 'webm';

        const blob = new Blob(chunks, { type: mimeType });
        const audioFile = new File([blob], `recording.${extension}`, { type: mimeType });

        // Enhanced validation for recorded audio
        if (audioFile.size === 0) {
          console.error('Empty audio file detected - no data recorded');
          toast.error('Recording failed - no audio data captured. Please ensure microphone access and try again.');
          return;
        }

        if (audioFile.size < 1000) { // Less than 1KB is likely invalid for WebM
          console.error('Audio file too small:', audioFile.size, 'bytes');
          toast.error('Recording too short or incomplete - please record for at least 1 second');
          return;
        }

        // Add user message with audio
        setMessages(prev => [...prev, {
          id: `user-audio-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'user',
          content: 'Voice message',
          audioFile: audioFile,
          timestamp: new Date()
        }]);

        // Process the audio with user feedback
        try {
        await processUserInput(audioFile, 'voice');
        } catch (error) {
          console.error('Voice processing failed:', error);
          toast.error('Failed to process voice message. Please try again.');
        }
      };

      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingStartTime(Date.now());

              // Start recording with periodic data requests
        try {
      recorder.start();

          // Add periodic data requests for reliable collection
          const dataRequestInterval = setInterval(() => {
            if (recorder && recorder.state === 'recording') {
              try {
                recorder.requestData();
              } catch (e) {
                // Silently handle request data errors
              }
            } else {
              clearInterval(dataRequestInterval);
            }
          }, 1000); // Request data every second

          // Store interval for cleanup
          recorder._dataRequestInterval = dataRequestInterval;

        } catch (error) {
          console.error('Failed to start recording:', error);
          toast.error('Failed to start recording. Please check microphone permissions.');
          return;
        }
    } catch (error) {
      toast.error('Could not access microphone');
      console.error('Recording error:', error);
    }
  };

  // Stop voice recording with minimum duration check
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      const recordingDuration = Date.now() - recordingStartTime;
      const MIN_RECORDING_TIME = 500; // Minimum 500ms

      if (recordingDuration < MIN_RECORDING_TIME) {
        toast.error('Recording too short - please record for at least 1 second');
        return;
      }


      // Clean up data request interval
      if (mediaRecorder._dataRequestInterval) {
        clearInterval(mediaRecorder._dataRequestInterval);
      }

      // Request any pending data before stopping
      if (mediaRecorder.state === 'recording') {
        try {
          mediaRecorder.requestData(); // Force collection of any pending data
        } catch (e) {
          console.warn('Could not request final data:', e);
        }
      }

      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setMediaRecorder(null);
      setRecordingStartTime(null);
    }
  };

  // Handle file drop
  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const audioFile = files.find(file => file.type.startsWith('audio/'));

    if (audioFile) {
      handleFileSelect(audioFile);
    } else {
      toast.error('Please upload an audio file');
    }
  };

  // Handle file select with comprehensive validation
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Comprehensive file validation
    const allowedTypes = [
      'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/m4a', 
      'audio/webm', 'audio/ogg', 'audio/mp4', 'audio/aac'
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const minSize = 100; // 100 bytes minimum

    if (!file.type.startsWith('audio/')) {
      toast.error('Please select an audio file');
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error(`Unsupported audio format: ${file.type}. Please use MP3, WAV, M4A, WebM, or OGG.`);
      return;
    }

    if (file.size > maxSize) {
      toast.error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB (max: 10MB)`);
      return;
    }

    if (file.size < minSize) {
      toast.error('File too small - please select a valid audio file');
      return;
    }

    // Add user message with uploaded file
    setMessages(prev => [...prev, {
      id: `user-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'user',
      content: `Uploaded: ${file.name}`,
      audioFile: file,
      timestamp: new Date()
    }]);

    // Process the file
    await processUserInput(file, 'voice');
  };

  // Circuit breaker for reliability
  const checkCircuitBreaker = () => {
    const errorThreshold = 3;
    const timeoutDuration = 60000; // 1 minute

    if (analysisErrors >= errorThreshold) {
      const timeSinceLastError = Date.now() - lastErrorTime;
      if (timeSinceLastError < timeoutDuration) {
        setIsCircuitOpen(true);
        return false; // Circuit is open
      } else {
        // Reset circuit breaker
        setAnalysisErrors(0);
        setIsCircuitOpen(false);
      }
    }
    return true; // Circuit is closed
  };

  const recordError = () => {
    setAnalysisErrors(prev => prev + 1);
    setLastErrorTime(Date.now());
  };

  const recordSuccess = () => {
    setAnalysisErrors(0);
    setIsCircuitOpen(false);
  };

  // Process user input (text or voice)
  const processUserInput = async (input, type) => {
    if (!checkCircuitBreaker()) {
      setMessages(prev => [...prev, {
        id: `bot-circuit-open-${Date.now()}`,
        type: 'bot',
        content: 'Analysis is temporarily unavailable. Please try again in a minute.',
        isError: true,
        timestamp: new Date()
      }]);
      return;
    }

    setIsProcessing(true);
    setCurrentOperation('analyzing');

    const thinkingMessageId = `bot-thinking-${Date.now()}`;
    const thinkingMessage = {
      id: thinkingMessageId,
      type: 'bot',
      content: 'Preparing your request...',
      isThinking: true,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, thinkingMessage]);

    // This is a non-blocking, self-invoking async function
    (async () => {
    try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        let textToAnalyze = input;
      if (type === 'voice') {
        setCurrentOperation('transcribing');
          updateThinkingMessage(thinkingMessageId, 'Transcribing audio...');
        const transcription = await uploadAndTranscribe(input);
          if (!transcription || !transcription.trim()) {
          throw new Error('No transcription received from audio');
          }
          textToAnalyze = transcription;
        }

        setCurrentOperation('analyzing');
        updateThinkingMessage(thinkingMessageId, 'Submitting for analysis...');

        const submissionTime = new Date();
        const jobId = await submitAnalysis(textToAnalyze, user.id);
        const result = await pollForResults(user.id, jobId, thinkingMessageId, submissionTime);
        const finalOperations = await transformAnalysisResults(result.data);

        updateMessageWithResults(thinkingMessageId, finalOperations);
        recordSuccess();

      } catch (error) {
        console.error('Processing error:', error);
        recordError();
        updateMessageWithError(thinkingMessageId, error.message);
      } finally {
        setIsProcessing(false);
        setCurrentOperation(null);
      }
    })();
  };

  const updateThinkingMessage = (messageId, content, progressData) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, content, ...progressData } 
        : msg
    ));
  };

  const updateMessageWithResults = (messageId, analysisResult) => {
    if (!analysisResult || analysisResult.length === 0) {
      setMessages(prev => prev.map(msg => msg.id === messageId ? {
        id: `bot-no-results-${Date.now()}`,
        type: 'bot',
        content: "I'm not sure what you want me to handle. Can you describe a customer question you get?",
        timestamp: new Date()
      } : msg));
      return;
    }

        setAnalysisResults(analysisResult);
        setCurrentCardIndex(0);

        const operationTypes = analysisResult.map(op => op.type);
        const hasNewCategory = operationTypes.includes('newCategory');
        const hasAddSetting = operationTypes.includes('addSetting');
        const hasEditSetting = operationTypes.includes('editSetting');
        const hasDeleteSetting = operationTypes.includes('deleteSetting');

        let message = "Got it. ";
    if (hasNewCategory && hasAddSetting) message += "I'll create the new category and add the responses:";
    else if (hasNewCategory) message += "I'll create that category for you:";
    else if (hasAddSetting && hasEditSetting) message += "I'll add the new responses and update the existing ones:";
    else if (hasAddSetting) message += "I'll add that for you:";
    else if (hasEditSetting) message += "I'll update that for you:";
    else if (hasDeleteSetting) message += "I'll remove that for you:";
    else message += "I'll handle this for you:";

    setMessages(prev => prev.map(msg => msg.id === messageId ? {
      id: `bot-analysis-${Date.now()}`,
          type: 'bot',
          content: message,
          analysisResults: analysisResult,
          timestamp: new Date()
    } : msg));
  };

  const updateMessageWithError = (messageId, errorMessageText) => {
    let finalMessage = 'Something went wrong. Please try again.';
    if (errorMessageText) {
        if (errorMessageText.includes('timeout')) finalMessage = 'Analysis is taking longer than expected. Please try again later.';
        else if (errorMessageText.includes('queue') || errorMessageText.includes('overload')) finalMessage = 'System is busy. Please try again in a few moments.';
        else if (errorMessageText.includes('transcription')) finalMessage = 'Could not understand the audio. Please try speaking more clearly or use text input.';
        else if (errorMessageText.includes('failed')) finalMessage = 'Analysis failed during processing. Please check the logs and try again.';
    }

    setMessages(prev => prev.map(msg => msg.id === messageId ? {
      id: `bot-error-${Date.now()}`,
        type: 'bot',
      content: finalMessage,
        isError: true,
        timestamp: new Date()
    } : msg));
  };

  // Upload and transcribe audio with robust error handling and retry logic
  const uploadAndTranscribe = async (audioFile, retryCount = 0) => {
    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 30000; // 30 seconds

    try {
      // Comprehensive validation
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

      // Validate audio file before upload
      if (!audioFile) throw new Error('No audio file provided');
      if (!(audioFile instanceof File || audioFile instanceof Blob)) {
        throw new Error('Invalid audio file format');
      }

      // File size validation (10MB limit with detailed error)
      const maxSize = 10 * 1024 * 1024;
      if (audioFile.size > maxSize) {
        throw new Error(`Audio file too large: ${(audioFile.size / 1024 / 1024).toFixed(2)}MB (max: 10MB)`);
      }

      if (audioFile.size === 0) {
        throw new Error('Audio file is empty');
      }


      // Create FormData with validation and proper filename
    const formData = new FormData();

      // Ensure proper filename and extension based on actual file type
      let filename = audioFile.name || 'recording';
      if (!filename.includes('.')) {
        const extension = audioFile.type.includes('webm') ? 'webm' : 
                         audioFile.type.includes('mp4') ? 'mp4' : 
                         audioFile.type.includes('ogg') ? 'ogg' :
                         audioFile.type.includes('wav') ? 'wav' : 'webm';
        filename += `.${extension}`;
      }

      formData.append('audio', audioFile, filename);
    formData.append('account_id', user.id);

      // Validate FormData
      if (!formData.has('audio') || !formData.has('account_id')) {
        throw new Error('FormData missing required fields');
      }

      // Create AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
    const response = await fetch(`${API_BASE_URL}${endpoints.voiceTranscribe}`, {
      method: 'POST',
          body: formData,
          signal: controller.signal,
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
    });

        clearTimeout(timeoutId);

        // Enhanced error handling with server response details
    if (!response.ok) {
          let errorDetails = `HTTP ${response.status} ${response.statusText}`;
          let serverMessage = '';

          try {
            const errorText = await response.text();
            console.error('Server error response:', errorText);

            // Try to parse as JSON for structured error
            try {
              const errorJson = JSON.parse(errorText);
              serverMessage = errorJson.message || errorJson.error || errorText;
            } catch {
              serverMessage = errorText;
            }

            errorDetails += serverMessage ? `: ${serverMessage}` : '';
          } catch (parseError) {
            console.error('Could not parse error response:', parseError);
          }

          throw new Error(`Upload failed - ${errorDetails}`);
    }

    const result = await response.json();

        // Robust response parsing with detailed error reporting
        const extractTranscription = (data) => {
          // Priority order for transcription extraction
          const paths = [
            'data.transcription',
            'transcription', 
            'data.text',
            'text',
            'data.transcript',
            'transcript'
          ];

          for (const path of paths) {
            const keys = path.split('.');
            let value = data;

            for (const key of keys) {
              value = value?.[key];
              if (value === undefined) break;
            }

            if (typeof value === 'string' && value.trim()) {
              return value.trim();
            }
          }

          return null;
        };

        const transcription = extractTranscription(result);

        if (!transcription) {
          console.error('No transcription found in response:', {
            responseKeys: Object.keys(result),
            fullResponse: result
          });
          throw new Error('No transcription text received from server');
        }

        return transcription;

      } catch (fetchError) {
        clearTimeout(timeoutId);

        if (fetchError.name === 'AbortError') {
          throw new Error(`Upload timeout after ${TIMEOUT_MS / 1000} seconds`);
        }

        throw fetchError;
      }

    } catch (error) {
      console.error(`Upload attempt ${retryCount + 1} failed:`, error);

      // Retry logic for transient errors
      const isRetryableError = (
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('timeout') ||
        error.message.includes('HTTP 5') // 5xx server errors
      );

      if (retryCount < MAX_RETRIES && isRetryableError) {
        const delayMs = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff

        await new Promise(resolve => setTimeout(resolve, delayMs));
        return uploadAndTranscribe(audioFile, retryCount + 1);
      }

      // Re-throw with enhanced error context
      const enhancedError = new Error(`Audio upload failed: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.retryCount = retryCount;
      throw enhancedError;
    }
  };

  // Analyze text with async processing
  const submitAnalysis = async (text, accountId) => {
    const response = await fetch(`${API_BASE_URL}${endpoints.voiceAnalyze}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, account_id: accountId })
    });

    if (!response.ok) throw new Error('Failed to submit analysis');

    const result = await response.json();
    if (!result.success) throw new Error(result.message || 'Analysis submission failed');

    return result.data.jobId;
  };

  const pollForResults = async (accountId, jobId, messageId, submissionTime) => {
    const maxAttempts = 90; // 3 minutes at 2-second intervals
    const pollInterval = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      try {
        const statusResponse = await fetch(`${API_BASE_URL}/api/brain/voice/analyze/${accountId}/status`);

        if (statusResponse.ok) {
          const { data: status } = await statusResponse.json();

          if (status.status === 'completed' && status.hasResults && new Date(status.lastAnalysis) > submissionTime) {
            const resultsResponse = await fetch(`${API_BASE_URL}/api/brain/voice/analyze/${accountId}/results`);
            if (resultsResponse.ok) return await resultsResponse.json();
          }

          if (status.status === 'failed') throw new Error('Analysis failed during processing');
        }

        const percentage = Math.round(((attempt + 1) / maxAttempts) * 100);
        updateThinkingMessage(messageId, `Analyzing... ${percentage}%`, { 
          progress: percentage, 
          timeElapsed: (attempt + 1) * (pollInterval / 1000) 
        });

      } catch (error) {
        console.error(`Poll attempt ${attempt + 1} failed:`, error);
        if (attempt === maxAttempts - 1) throw new Error('Analysis timeout - please try again');
      }
    }
    throw new Error('Analysis timeout - please try again');
  };

  const transformAnalysisResults = async (data) => {
    const operations = [];

    if (data.newCategories) {
      data.newCategories.forEach((category, index) => operations.push({
        id: `new-cat-${index}`, type: 'newCategory', operation: 'ADD CATEGORY',
        title: category.name, description: category.description, data: category
      }));
    }

    if (data.addSettings) {
      data.addSettings.forEach((setting, index) => {
        const operation = {
          id: `add-${index}`, type: 'addSetting', operation: 'ADD SETTING',
          title: setting.name || 'New Setting', description: setting.description, data: setting
        };
        if (setting.type === 'complex_answer') {
          const placeholders = detectPlaceholders(setting);
          if (placeholders.length > 0) operation.placeholders = placeholders;
          if (setting.answer_type === 'api' && setting.api_config) operation.isApiBasedAnswer = true;
        }
        operations.push(operation);
      });
    }

    if (data.editSettings) {
      const settingIds = data.editSettings.map(edit => edit.id).filter(Boolean);
      const fetchedSettings = await fetchCurrentSettingsData(settingIds);
      data.editSettings.forEach((edit, index) => {
        const current = fetchedSettings[edit.id];
        operations.push({
          id: `edit-${index}`, type: 'editSetting', operation: 'EDIT SETTING',
          title: current?.name || edit.name || 'Setting', description: '',
          data: { ...edit, type: current?.type, setting_type: current?.type, name: current?.name }
        });
      });
    }

    if (data.deleteSettings) {
      const settingIds = data.deleteSettings.map(d => d.id).filter(Boolean);
      const fetchedSettings = await fetchCurrentSettingsData(settingIds);
      data.deleteSettings.forEach((deletion, index) => operations.push({
        id: `delete-${index}`, type: 'deleteSetting', operation: 'DELETE SETTING',
        title: fetchedSettings[deletion.id]?.name || deletion.id || 'Setting',
        description: deletion.reason || 'No reason provided', data: deletion
      }));
    }

    return operations;
  };

  // Helper function to detect placeholders in complex answers
  const detectPlaceholders = (setting) => {
    const placeholders = [];
    const placeholderRegex = /<([^>]+)>/g;
    const settingStr = JSON.stringify(setting);
    let match;

    while ((match = placeholderRegex.exec(settingStr)) !== null) {
      const field = match[1];
      if (!placeholders.includes(field)) {
        placeholders.push(field);
      }
    }

    return placeholders;
  };

  // Helper function to get placeholder field type
  const getPlaceholderFieldType = (placeholder) => {
    const lowerPlaceholder = placeholder.toLowerCase();

    if (lowerPlaceholder.includes('email')) return 'email';
    if (lowerPlaceholder.includes('phone') || lowerPlaceholder.includes('number')) return 'tel';
    if (lowerPlaceholder.includes('url') || lowerPlaceholder.includes('endpoint')) return 'url';
    if (lowerPlaceholder.includes('password') || lowerPlaceholder.includes('token')) return 'password';

    return 'text';
  };

  // Helper function to get placeholder field label
  const getPlaceholderFieldLabel = (placeholder) => {
    return placeholder
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  };

  // Helper function to get placeholder field example
  const getPlaceholderFieldExample = (placeholder) => {
    const examples = {
      'API_ENDPOINT_URL': 'https://api.example.com/v1/endpoint',
      'AUTH_TOKEN': 'your-auth-token-here',
      'PHONE_NUMBER': '+1234567890',
      'EMAIL_ADDRESS': 'admin@company.com',
      'SMS_MESSAGE_CONTENT': 'Your message content here...',
      'TRANSFER_PHONE_NUMBER': '+1234567890'
    };

    return examples[placeholder] || `Enter ${getPlaceholderFieldLabel(placeholder).toLowerCase()}`;
  };

  // Handle editing settings with placeholder replacements
  const handleFieldEdit = (settingIndex, field, value) => {
    setEditingSettings(prev => ({
      ...prev,
      [settingIndex]: {
        ...prev[settingIndex],
        [field]: value
      }
    }));
  };

    // Fetch current setting data for diff display
  const fetchCurrentSettingsData = async (settingIds) => {
    try {
      const { data: settingsData, error } = await supabase
        .from('brain_settings')
        .select('id, name, description, type')
        .in('id', settingIds);

      if (error) throw error;

      const currentData = {};

      // Process each setting based on its type
      for (const setting of settingsData) {
        currentData[setting.id] = {
          name: setting.name,
          description: setting.description,
          type: setting.type
        };

        // Fetch type-specific data based on setting type
        if (setting.type === 'simple_answer') {
          const { data: answerData, error: answerError } = await supabase
            .from('brain_simple_answers')
            .select('content, title')
            .eq('settings_id', setting.id)
            .single();

          if (!answerError && answerData) {
            currentData[setting.id] = {
              ...currentData[setting.id],
              content: answerData.content,
              title: answerData.title,
              answer: answerData.content // Map content to answer for consistency
            };
          }
        } else if (setting.type === 'complex_answer') {
          const { data: complexData, error: complexError } = await supabase
            .from('brain_complex_answers')
            .select('content, title')
            .eq('settings_id', setting.id)
            .single();

          if (!complexError && complexData) {
            currentData[setting.id] = {
              ...currentData[setting.id],
              content: complexData.content,
              title: complexData.title,
              answer: complexData.content
            };
          }
        } else if (setting.type === 'simple_action') {
          const { data: actionData, error: actionError } = await supabase
            .from('brain_simple_actions')
            .select('*')
            .eq('settings_id', setting.id)
            .single();

          if (!actionError && actionData) {
            currentData[setting.id] = {
              ...currentData[setting.id],
              action_type: actionData.action_type,
              api_url: actionData.api_url,
              api_method: actionData.api_method,
              api_headers: actionData.api_headers,
              api_body: actionData.api_body,
              email_addresses: actionData.email_addresses,
              email_subject: actionData.email_subject,
              email_message: actionData.email_message,
              sms_numbers: actionData.sms_numbers,
              sms_message: actionData.sms_message,
              transfer_number: actionData.transfer_number,
              transfer_name: actionData.transfer_name,
              dtmf_number: actionData.dtmf_number
            };
          }
        }
      }

      setCurrentSettingsData(prev => ({ ...prev, ...currentData }));
      return currentData; // Return the data so it can be used immediately
    } catch (error) {
      console.error('Error fetching current settings data:', error);
      return {}; // Return empty object on error
    }
  };

  // Handle card approval - immediately save to database with placeholder processing
  const handleApproveCard = async (operation, userEdits = {}) => {
    try {
      // Update UI immediately to show approval and saving state
      setMessages(prev => prev.map(msg => {
        if (msg.analysisResults) {
          const updatedResults = msg.analysisResults.map(op => 
            op.id === operation.id ? { ...op, approved: true, saving: true } : op
          );
          return { ...msg, analysisResults: updatedResults };
        }
        return msg;
      }));

      // Save to database immediately
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const saveData = {
        account_id: user.id,
        new_categories: [],
        add_settings: [],
        edit_settings: [],
        delete_settings: []
      };

      // Add this single operation to the appropriate array with placeholder processing
      switch (operation.type) {
        case 'newCategory':
          saveData.new_categories.push(operation.data);
          break;
        case 'addSetting':
          // Process settings with placeholder replacements
          let settingData = {
            ...operation.data,
            name: operation.data.name || operation.data.title || 'Untitled Setting'
          };

          // Apply placeholder replacements if any
          if (operation.hasPlaceholders && userEdits) {
            settingData = applyPlaceholderReplacements(settingData, userEdits);
          }

          saveData.add_settings.push(settingData);
          break;
        case 'editSetting':
          saveData.edit_settings.push(operation.data);
          break;
        case 'deleteSetting':
          // New API expects just the UUID, not an object
          const deleteId = operation.data?.id || operation.data;
          saveData.delete_settings.push(deleteId);
          break;
      }

      // Call the save API
      const response = await fetch(`${API_BASE_URL}${endpoints.voiceSaveSettings}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveData)
      });

      if (!response.ok) {
        throw new Error('Failed to save setting');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Save failed');
      }

      // Update UI to show success
      setMessages(prev => prev.map(msg => {
        if (msg.analysisResults) {
          const updatedResults = msg.analysisResults.map(op => 
            op.id === operation.id ? { ...op, approved: true, saving: false, saved: true } : op
          );
          return { ...msg, analysisResults: updatedResults };
        }
        return msg;
      }));

      // Call parent callback to update the brain UI locally (no page refresh)
      if (onResultsApproved) {
        // Structure the data to match what Brain.js expects (same as VoiceUploadModal)
        onResultsApproved({
          saveResults: {
            results: result.data
          }
        });
      }

      toast.success('Response saved successfully!');

    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save response');

      // Update UI to show error
      setMessages(prev => prev.map(msg => {
        if (msg.analysisResults) {
          const updatedResults = msg.analysisResults.map(op => 
            op.id === operation.id ? { ...op, approved: false, saving: false, error: true } : op
          );
          return { ...msg, analysisResults: updatedResults };
        }
        return msg;
      }));
    }
  };

  // Apply placeholder replacements to setting data
  const applyPlaceholderReplacements = (settingData, userEdits) => {
    const processedData = JSON.parse(JSON.stringify(settingData));

    // Replace placeholders with user input
    const settingStr = JSON.stringify(processedData);
    let updatedStr = settingStr;

    Object.entries(userEdits).forEach(([placeholder, value]) => {
      const regex = new RegExp(`<${placeholder}>`, 'g');
      updatedStr = updatedStr.replace(regex, value);
    });

    return JSON.parse(updatedStr);
  };

  // Handle card denial
  const handleDenyCard = (operation) => {
    setDeniedOperations(prev => [...prev, operation]);

    // Update the message to show denial
    setMessages(prev => prev.map(msg => {
      if (msg.analysisResults) {
        const updatedResults = msg.analysisResults.map(op => 
          op === operation ? { ...op, denied: true } : op
        );
        return { ...msg, analysisResults: updatedResults };
      }
      return msg;
    }));
  };



  // Play audio message
  const playAudio = (audioFile) => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(URL.createObjectURL(audioFile));
    audio.onended = () => {
      setIsPlaying(false);
      setCurrentAudio(null);
    };

    audio.play();
    setCurrentAudio(audio);
    setIsPlaying(true);
  };

  // Clear chat
  const clearChat = () => {
    setMessages([]);
    setAnalysisResults(null);
    setDeniedOperations([]);
    setCurrentCardIndex(0);
  };

  if (!isOpen) return null;

  return (
    <div className={cn(
      "flex flex-col h-full bg-gray-50 border-l border-gray-200/50 brain-chat-interface",
      className
    )}>
      {/* Elegant Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between p-6 border-b border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner">
                            <AndroidLogo className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex items-center space-x-2">
          <h2 className="font-light text-gray-900 text-lg tracking-tight">Teach Bella</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 font-medium border border-amber-200/50 shadow-inner cursor-help">
                    Beta
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="font-medium text-gray-900 mb-1">Beta Feature</div>
                  <div className="text-gray-600 leading-relaxed">
                    This is experimental. For best results, submit one simple request at a time. 
                    Complex or lengthy requests may not work reliably yet.
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {messages.length > 0 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={clearChat}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-white/60 transition-colors"
              title="Clear conversation"
            >
              <Trash className="w-4 h-4" />
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-white/60 transition-colors"
            title="Close Bella"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </div>
      </motion.div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="h-full flex flex-col justify-center text-center max-w-md mx-auto"
          >
            {/* Elegant Icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner mx-auto mb-6"
            >
                              <AndroidLogo className="w-8 h-8 text-blue-600" />
            </motion.div>

            {/* Cinematic Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="mb-8"
            >
              <h3 className="font-light text-gray-900 text-xl mb-3 tracking-tight">Teach Bella</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Tell me about customer questions or business processes you'd like to automate.
              </p>
            </motion.div>

            {/* Elegant Feature List */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="space-y-3"
            >
              {[
                { icon: Chat, text: "Type your message below" },
                { icon: Microphone, text: "Record a voice message" },
                { icon: Upload, text: "Upload an audio file" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                  className="flex items-center space-x-3 text-gray-500"
                >
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-inner">
                    <item.icon className="w-3 h-3 text-gray-600" />
                  </div>
                  <span className="text-xs">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        ) : (
          messages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <MessageBubble
                message={message}
                onApprove={handleApproveCard}
                onDeny={handleDenyCard}
                onPlayAudio={playAudio}
                isPlaying={isPlaying}
                currentAudio={currentAudio}
                currentSettingsData={currentSettingsData}
              />
            </motion.div>
          ))
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Elegant Input Area */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="border-t border-gray-200/50 p-6 bg-white/70 backdrop-blur-sm"
      >
        <div
          className={cn(
            "relative rounded-2xl border border-gray-200/50 bg-white/70 backdrop-blur-sm shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300",
            dragActive ? "border-blue-300 bg-blue-50/50 shadow-[0_8px_30px_rgb(59,130,246,0.15)]" : "",
            isProcessing ? "opacity-75" : ""
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleFileDrop}
        >
          {/* Input Content */}
          <div className="space-y-4 p-4">
            {/* Text Area */}
            <div className="w-full">
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleTextSubmit();
                  }
                }}
                onWheel={(e) => {
                  const textarea = e.currentTarget;
                  if (textarea.scrollHeight > textarea.clientHeight) {
                    e.stopPropagation();
                  }
                }}
                placeholder="Describe a customer question or business process..."
                className="w-full resize-none border-0 focus:ring-0 text-sm placeholder-gray-400 bg-transparent"
                rows={1}
                disabled={isProcessing}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end">
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                />

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing}
                  className="p-2 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 text-gray-600 hover:from-gray-100 hover:to-gray-200 shadow-inner disabled:opacity-50 transition-all"
                  title="Upload audio file"
                >
                  <Upload className="w-4 h-4" />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                  className={cn(
                    "p-2 rounded-lg shadow-inner disabled:opacity-50 transition-all",
                    isRecording
                      ? "bg-gradient-to-br from-red-50 to-red-100 text-red-600 hover:from-red-100 hover:to-red-200"
                      : "bg-gradient-to-br from-gray-50 to-gray-100 text-gray-600 hover:from-gray-100 hover:to-gray-200"
                  )}
                  title={isRecording ? "Stop recording" : "Start recording"}
                >
                  {isRecording ? <MicrophoneSlash className="w-4 h-4" /> : <Microphone className="w-4 h-4" />}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleTextSubmit}
                  disabled={!inputText.trim() || isProcessing}
                  className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 hover:from-blue-100 hover:to-blue-200 shadow-inner disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Send message"
                >
                  <PaperPlaneTilt className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </div>

          {/* Recording Timer */}
          <AnimatePresence>
            {isRecording && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center justify-center py-3 text-sm text-red-600 bg-red-50/50 border-t border-red-100/50 rounded-b-2xl"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="w-2 h-2 bg-red-600 rounded-full mr-2"
                />
                <span className="font-medium">Recording: {formatRecordingTime(recordingTime)}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Drop Zone Overlay */}
          <AnimatePresence>
            {dragActive && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-blue-50/90 backdrop-blur-sm rounded-2xl"
              >
                <motion.div
                  initial={{ scale: 0.8, y: 10 }}
                  animate={{ scale: 1, y: 0 }}
                  className="text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner mx-auto mb-3">
                    <Upload className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-blue-600 font-medium text-sm">Drop audio file here</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ 
  message, 
  onApprove, 
  onDeny, 
  onPlayAudio, 
  isPlaying, 
  currentAudio,
  currentSettingsData 
}) => {
  const isUser = message.type === 'user';

  return (
    <div className={cn(
      "flex",
      isUser ? "justify-end" : "justify-start"
    )}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={cn(
          "rounded-2xl px-4 py-3 shadow-[0_4px_20px_rgb(0,0,0,0.08)]",
          isUser 
            ? "max-w-[80%] bg-gradient-to-br from-blue-500 to-blue-600 text-white" 
            : message.isError
            ? "w-full bg-gradient-to-br from-red-50 to-red-100 text-red-800 border border-red-200/50"
            : message.isSuccess
            ? "w-full bg-gradient-to-br from-green-50 to-green-100 text-green-800 border border-green-200/50"
            : "w-full bg-white/80 backdrop-blur-sm text-gray-900 border border-gray-200/50"
        )}
      >
        {/* Message Header */}
        <div className="flex items-center space-x-2 mb-2">
          <div className={cn(
            "w-6 h-6 rounded-lg flex items-center justify-center shadow-inner",
            isUser 
              ? "bg-white/20" 
              : message.isError
              ? "bg-red-100"
              : message.isSuccess
              ? "bg-green-100"
              : "bg-gradient-to-br from-gray-50 to-gray-100"
          )}>
            {isUser ? (
              <User className="w-3 h-3" />
            ) : (
              <AndroidLogo className="w-3 h-3" />
            )}
          </div>
          <span className="text-xs font-medium opacity-75">
            {isUser ? 'You' : 'Bella'}
          </span>
        </div>

        {/* Message Content */}
        <div className="space-y-3">
          <p className="text-sm leading-relaxed">{message.content}</p>

          {/* Audio Player */}
          {message.audioFile && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onPlayAudio(message.audioFile)}
              className={cn(
                "flex items-center space-x-2 text-xs rounded-lg px-3 py-2 transition-all shadow-inner",
                isUser 
                  ? "bg-white/20 hover:bg-white/30 text-white" 
                  : "bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 text-gray-700"
              )}
            >
              <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner">
                {isPlaying ? <SpeakerX className="w-2 h-2 text-blue-600" /> : <SpeakerHigh className="w-2 h-2 text-blue-600" />}
              </div>
              <span>Play Audio</span>
            </motion.button>
          )}

          {/* Thinking Indicator with Progress */}
          {message.isThinking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2 text-xs">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner">
                <CircleNotch className="w-2 h-2 animate-spin text-blue-600" />
              </div>
                <span className="opacity-75">
                  {message.content || 'Starting analysis...'}
                </span>
              </div>

              {/* Progress Bar */}
              {message.progress !== undefined && (
                <div className="space-y-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                    <motion.div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${message.progress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                  <div className="flex justify-between text-xs opacity-60">
                    <span>Processing with AI...</span>
                    {message.timeElapsed && (
                      <span>{message.timeElapsed}s elapsed</span>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Analysis Results */}
          {message.analysisResults && (
            <div className="space-y-4 mt-4">
              {message.analysisResults.map((operation, index) => (
                <motion.div
                  key={operation.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <OperationCard
                    operation={operation}
                    onApprove={(op) => onApprove(op)}
                    onDeny={(op) => onDeny(op)}
                    currentSettingsData={currentSettingsData}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Simple diff function for GitHub-style changes
const createDiff = (oldText, newText) => {
  if (!oldText && !newText) return [];
  if (!oldText) return [{ type: 'added', content: newText }];
  if (!newText) return [{ type: 'removed', content: oldText }];

  // Simple word-based diff for better readability
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);

  // Find common prefix and suffix
  let prefixEnd = 0;
  while (prefixEnd < Math.min(oldWords.length, newWords.length) && 
         oldWords[prefixEnd] === newWords[prefixEnd]) {
    prefixEnd++;
  }

  let suffixStart = Math.max(prefixEnd, Math.min(oldWords.length, newWords.length));
  let oldSuffixStart = oldWords.length;
  let newSuffixStart = newWords.length;

  while (oldSuffixStart > prefixEnd && newSuffixStart > prefixEnd &&
         oldWords[oldSuffixStart - 1] === newWords[newSuffixStart - 1]) {
    oldSuffixStart--;
    newSuffixStart--;
  }

  const changes = [];

  // Add context before changes (up to 5 words)
  if (prefixEnd > 0) {
    const contextStart = Math.max(0, prefixEnd - 5);
    const contextWords = oldWords.slice(contextStart, prefixEnd);
    if (contextWords.length > 0) {
      changes.push({ type: 'context', content: contextWords.join('') });
    }
    if (contextStart > 0) {
      changes.push({ type: 'context', content: '...' });
    }
  }

  // Add removed content
  if (oldSuffixStart > prefixEnd) {
    const removedWords = oldWords.slice(prefixEnd, oldSuffixStart);
    changes.push({ type: 'removed', content: removedWords.join('') });
  }

  // Add added content
  if (newSuffixStart > prefixEnd) {
    const addedWords = newWords.slice(prefixEnd, newSuffixStart);
    changes.push({ type: 'added', content: addedWords.join('') });
  }

  // Add context after changes (up to 5 words)
  if (oldSuffixStart < oldWords.length) {
    const contextEnd = Math.min(oldWords.length, oldSuffixStart + 5);
    const contextWords = oldWords.slice(oldSuffixStart, contextEnd);
    if (contextWords.length > 0) {
      changes.push({ type: 'context', content: contextWords.join('') });
    }
    if (contextEnd < oldWords.length) {
      changes.push({ type: 'context', content: '...' });
    }
  }

  return changes;
};

// Operation Card Component
const OperationCard = ({ operation, onApprove, onDeny, currentSettingsData }) => {
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [detectedPlaceholders, setDetectedPlaceholders] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [complexAnswerEdits, setComplexAnswerEdits] = useState({});
  const [showApiConfig, setShowApiConfig] = useState(false);

  // Helper functions for complex answer placeholders
  const getPlaceholderFieldType = (placeholder) => {
    const lowerPlaceholder = placeholder.toLowerCase();

    if (lowerPlaceholder.includes('email')) return 'email';
    if (lowerPlaceholder.includes('phone') || lowerPlaceholder.includes('number')) return 'tel';
    if (lowerPlaceholder.includes('url') || lowerPlaceholder.includes('endpoint')) return 'url';
    if (lowerPlaceholder.includes('password') || lowerPlaceholder.includes('token')) return 'password';

    return 'text';
  };

  const getPlaceholderFieldExample = (placeholder) => {
    const examples = {
      'API_ENDPOINT_URL': 'https://api.example.com/v1/endpoint',
      'AUTH_TOKEN': 'your-auth-token-here',
      'PHONE_NUMBER': '+1234567890',
      'EMAIL_ADDRESS': 'admin@company.com',
      'SMS_MESSAGE_CONTENT': 'Your message content here...',
      'TRANSFER_PHONE_NUMBER': '+1234567890'
    };

    return examples[placeholder] || `Enter ${placeholder.replace(/_/g, ' ').toLowerCase()}`;
  };

  // Phone number formatting functions (reused from CallActionSettingsModal)
  const formatPhoneNumberAsUserTypes = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 10);

    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  const convertToE164 = (formattedNumber) => {
    if (!formattedNumber) return '';
    const cleaned = formattedNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    return formattedNumber;
  };

  // Determine placeholder type based on name
  const getPlaceholderType = (name) => {
    const lowerName = name.toLowerCase();

    // Phone number types - comprehensive detection for transfer operations
    if (lowerName.includes('phone') || 
        lowerName === 'phone_number' ||
        lowerName === 'transfer_number' ||
        lowerName === 'dtmf_number' ||
        lowerName.includes('transfer_phone') ||
        lowerName.includes('sms_number')) {
      return 'phone';
    }

    // Phone number array
    if (lowerName === 'phone_numbers' || 
        lowerName === 'sms_numbers') {
      return 'phone_array';
    }

    // Time types
    if (lowerName.includes('time') || lowerName === 'start_time' || lowerName === 'end_time') {
      return 'time';
    }

    // Days of week
    if (lowerName.includes('days') || lowerName === 'days_array') {
      return 'days';
    }

    // Email types
    if (lowerName.includes('email') || lowerName === 'email_addresses') {
      return 'email';
    }

    // JSON types
    if (lowerName.includes('body') || lowerName === 'request_body' || lowerName.includes('json')) {
      return 'json';
    }

    // Enum types (API methods)
    if (lowerName.includes('method') || lowerName === 'api_method') {
      return 'enum';
    }

    // Default to text
    return 'text';
  };

  // Detect placeholders in operation data on mount
  useEffect(() => {
    const placeholders = [];
    const processValue = (value, path = '') => {
      if (typeof value === 'string') {
        const matches = value.match(/<([^<>]+)>/g);
        if (matches) {
          matches.forEach(match => {
            const placeholder = match.slice(1, -1); // Remove < >
            const id = path ? `${path}.${placeholder}` : placeholder;
            if (!placeholders.find(p => p.id === id)) {
              const type = getPlaceholderType(placeholder);
              placeholders.push({
                id,
                placeholder,
                label: placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                path,
                originalMatch: match,
                type
              });
            }
          });
        }
      } else if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([key, val]) => {
          processValue(val, path ? `${path}.${key}` : key);
        });
      }
    };

    processValue(operation.data);
    setDetectedPlaceholders(placeholders);

    // Initialize placeholder values with appropriate defaults
    const initialValues = {};
    placeholders.forEach(p => {
      switch (p.type) {
        case 'days':
          initialValues[p.id] = [];
          break;
        case 'phone_array':
          initialValues[p.id] = [];
          break;
        case 'json':
          initialValues[p.id] = '{}';
          break;
        case 'enum':
          initialValues[p.id] = 'GET'; // Default for API methods
          break;
        case 'time':
          initialValues[p.id] = '09:00';
          break;
        default:
          initialValues[p.id] = '';
      }
    });
    setPlaceholderValues(initialValues);

    // Handle complex answer placeholders and API config
    let complexInitialValues = {};

    // Initialize placeholder values if needed
    if (operation.hasPlaceholders && operation.placeholders) {
      operation.placeholders.forEach(placeholder => {
        complexInitialValues[placeholder] = '';
      });
    }

    // Initialize API config if this is an API-based answer
    if (operation.isApiBasedAnswer && operation.apiConfig) {
      const apiInitialValues = {
        api_endpoint: operation.apiConfig.endpoint || '',
        api_method: operation.apiConfig.method || 'POST',
        auth_type: operation.apiConfig.auth_type || 'none',
        auth_token: operation.apiConfig.auth_config?.token || '',
        api_body_template: operation.apiConfig.body_template ? JSON.stringify(operation.apiConfig.body_template, null, 2) : '{}',
        api_response_template: operation.apiConfig.response_template || '',
        ...complexInitialValues
      };
      setComplexAnswerEdits(apiInitialValues);
    } else if (operation.hasPlaceholders && operation.placeholders) {
      // Set placeholder values only if not API-based
      setComplexAnswerEdits(complexInitialValues);
    }
  }, [operation.data, operation.hasPlaceholders, operation.isApiBasedAnswer]);

  const handlePlaceholderChange = (placeholderId, value) => {
    setPlaceholderValues(prev => ({
      ...prev,
      [placeholderId]: value
    }));
  };

  // Handle complex answer field changes
  const handleComplexAnswerEdit = (field, value) => {
    setComplexAnswerEdits(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const areAllPlaceholdersFilled = () => {
    // Check traditional placeholders
    const traditionalPlaceholdersFilled = detectedPlaceholders.every(p => {
      const value = placeholderValues[p.id];
      switch (p.type) {
        case 'days':
          return Array.isArray(value) && value.length > 0;
        case 'phone_array':
          return Array.isArray(value) && value.length > 0 && value.every(phone => phone?.trim());
        case 'json':
          try {
            JSON.parse(value);
            return value.trim() !== '' && value !== '{}';
          } catch {
            return false;
          }
        default:
          return value?.trim();
      }
    });

    // Check complex answer placeholders
    const complexPlaceholdersFilled = !operation.hasPlaceholders || operation.placeholders.every(placeholder => {
      return complexAnswerEdits[placeholder]?.trim();
    });

    // Check API configuration if required
    const apiConfigFilled = !operation.isApiBasedAnswer || (
      complexAnswerEdits.api_endpoint?.trim() &&
      complexAnswerEdits.api_method?.trim()
    );

    return traditionalPlaceholdersFilled && complexPlaceholdersFilled && apiConfigFilled;
  };

  const getOperationDataWithReplacements = () => {
    if (detectedPlaceholders.length === 0) return operation;

    const replaceInObject = (obj) => {
      if (typeof obj === 'string') {
        let result = obj;

        // Process each placeholder, but stop if result becomes non-string
        for (const p of detectedPlaceholders) {
          // Skip if result is no longer a string (already replaced with array/object)
          if (typeof result !== 'string') break;

          let replacementValue = placeholderValues[p.id];

          // Format replacement value based on type
          switch (p.type) {
            case 'phone':
              replacementValue = convertToE164(replacementValue);
              break;
            case 'phone_array':
              replacementValue = Array.isArray(replacementValue) ? replacementValue.map(convertToE164) : [];
              break;
            case 'days':
              replacementValue = replacementValue || [];
              break;
            case 'json':
              try {
                replacementValue = JSON.parse(replacementValue);
              } catch {
                replacementValue = {};
              }
              break;
            default:
              // Use as-is for text, email, time, enum
              break;
          }

          // For arrays and objects, don't use string replacement as it converts to string
          if (Array.isArray(replacementValue) || (typeof replacementValue === 'object' && replacementValue !== null)) {
            // Only replace if this string exactly matches the placeholder
            if (result === p.originalMatch) {
              result = replacementValue;
              break; // Stop processing since result is now non-string
            }
          } else {
            // For strings, use the original string replacement method
            result = result.replace(new RegExp(p.originalMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacementValue || p.originalMatch);
          }
        }

        return result;
      } else if (Array.isArray(obj)) {
        return obj.map(replaceInObject);
      } else if (typeof obj === 'object' && obj !== null) {
        const result = {};
        Object.entries(obj).forEach(([key, value]) => {
          result[key] = replaceInObject(value);
        });
        return result;
      }
      return obj;
    };

    return {
      ...operation,
      data: replaceInObject(operation.data)
    };
  };

  const handleApproveWithPlaceholders = () => {
    const updatedOperation = getOperationDataWithReplacements();
    console.log('Original operation:', operation);
    console.log('Placeholder values:', placeholderValues);
    console.log('Complex answer edits:', complexAnswerEdits);
    console.log('Updated operation with replacements:', updatedOperation);

    // Pass complex answer edits for processing
    onApprove(updatedOperation, complexAnswerEdits);
  };

  const getOperationIcon = (operation) => {
    const { type, data } = operation;

    switch (type) {
      case 'newCategory':
        return <FolderPlus className="w-4 h-4 text-yellow-700" />;
      case 'addSetting':
        // Use same icons as BrainActionModal based on setting type
        const settingType = data?.type;
        if (settingType === 'simple_answer') {
          return <BookOpen className="w-4 h-4 text-blue-700" />;
        } else if (settingType === 'complex_answer') {
          return <Database className="w-4 h-4 text-purple-700" />;
        } else if (settingType === 'simple_action') {
          return <Wrench className="w-4 h-4 text-amber-700" />;
        }
        return <BookOpen className="w-4 h-4 text-blue-700" />;
      case 'editSetting':
        // Use the same icon as the setting type for consistency
        const editSettingType = data?.type || data?.setting_type;
        if (editSettingType === 'simple_answer') {
          return <BookOpen className="w-4 h-4 text-blue-700" />;
        } else if (editSettingType === 'complex_answer') {
          return <Database className="w-4 h-4 text-purple-700" />;
        } else if (editSettingType === 'simple_action') {
          return <Wrench className="w-4 h-4 text-amber-700" />;
        }
        return <PencilSimple className="w-4 h-4 text-gray-600" />;
      case 'deleteSetting':
        return <Trash className="w-4 h-4 text-red-600" />;
      default:
        return <Plus className="w-4 h-4" />;
    }
  };

  const getOperationTitle = (operation) => {
    switch (operation.type) {
      case 'newCategory':
        return operation.data?.name || operation.title || 'New Category';
      case 'addSetting':
        return operation.data?.name || operation.data?.title || operation.title || 'New Response';
      case 'editSetting':
        // Show just the setting name without "Update" prefix
        const settingName = operation.data?.name || operation.data?.title || operation.title || 'Setting';
        return settingName;
      case 'deleteSetting':
        return `Delete: ${operation.title || 'Setting'}`;
      default:
        return operation.title || 'New Operation';
    }
  };

  const getOperationDescription = (operation) => {
    switch (operation.type) {
      case 'newCategory':
        return operation.data?.description || 'New folder to organize responses';
      case 'addSetting':
        const settingType = operation.data?.type;
        if (settingType === 'simple_answer') {
          return operation.data?.answer || operation.data?.description || 'New response for customers';
        } else if (settingType === 'complex_answer') {
          return operation.data?.answer || operation.data?.description || 'Smart response with follow-up questions';
        } else if (settingType === 'simple_action') {
          const actionType = operation.data?.action_type;
          return `${actionType?.toUpperCase() || 'ACTION'}: ${operation.data?.description || 'Automatic action'}`;
        }
        return operation.data?.description || 'New customer response';
      case 'editSetting':
        return '';
      case 'deleteSetting':
        return operation.data?.reason || operation.description || 'Remove this item';
      default:
        return operation.description || 'Business update';
    }
  };

  // Render specialized input based on placeholder type
  const renderPlaceholderInput = (placeholder) => {
    const { id, label, type } = placeholder;
    const value = placeholderValues[id];

    const inputClassName = "w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 bg-white/70 backdrop-blur-sm transition-all";

    switch (type) {
      case 'phone':
        return (
          <input
            key={id}
            type="tel"
            value={value}
            onChange={(e) => handlePlaceholderChange(id, formatPhoneNumberAsUserTypes(e.target.value))}
            placeholder="(555) 123-4567"
            className={inputClassName}
          />
        );

      case 'phone_array':
        return (
          <div key={id} className="space-y-2">
            {(value || []).map((phone, index) => (
              <div key={index} className="flex space-x-2">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const newPhones = [...(value || [])];
                    newPhones[index] = formatPhoneNumberAsUserTypes(e.target.value);
                    handlePlaceholderChange(id, newPhones);
                  }}
                  placeholder="(555) 123-4567"
                  className="flex-1 px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 bg-white/70 backdrop-blur-sm transition-all"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => {
                    const newPhones = (value || []).filter((_, i) => i !== index);
                    handlePlaceholderChange(id, newPhones);
                  }}
                  className="px-2 py-2 text-red-600 hover:bg-red-50 rounded-lg bg-gradient-to-br from-red-50 to-red-100 shadow-inner transition-all"
                >
                  ×
                </motion.button>
              </div>
            ))}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => handlePlaceholderChange(id, [...(value || []), ''])}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              + Add phone number
            </motion.button>
          </div>
        );

      case 'time':
        return (
          <input
            key={id}
            type="time"
            value={value}
            onChange={(e) => handlePlaceholderChange(id, e.target.value)}
            className={inputClassName}
          />
        );

      case 'days':
        return (
          <div key={id} className="flex flex-wrap gap-2">
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
              <motion.button
                key={day}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={() => {
                  const currentDays = value || [];
                  const newDays = currentDays.includes(index)
                    ? currentDays.filter(d => d !== index)
                    : [...currentDays, index].sort();
                  handlePlaceholderChange(id, newDays);
                }}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all shadow-inner",
                  (value || []).includes(index)
                    ? "bg-gradient-to-br from-blue-100 to-blue-200 text-blue-800 border border-blue-300/50"
                    : "bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 border border-gray-200/50 hover:from-gray-100 hover:to-gray-200"
                )}
              >
                {day.slice(0, 3)}
              </motion.button>
            ))}
          </div>
        );

      case 'email':
        return (
          <input
            key={id}
            type="email"
            value={value}
            onChange={(e) => handlePlaceholderChange(id, e.target.value)}
            placeholder="example@domain.com"
            className={inputClassName}
          />
        );

      case 'json':
        return (
          <textarea
            key={id}
            value={value}
            onChange={(e) => handlePlaceholderChange(id, e.target.value)}
            placeholder='{"key": "value"}'
            rows={3}
            className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 font-mono bg-white/70 backdrop-blur-sm transition-all"
          />
        );

      case 'enum':
        return (
          <div key={id} className="flex space-x-2">
            {['GET', 'POST', 'PUT', 'DELETE'].map(method => (
              <motion.button
                key={method}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={() => handlePlaceholderChange(id, method)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all shadow-inner",
                  value === method
                    ? "bg-gradient-to-br from-purple-100 to-purple-200 text-purple-800 border border-purple-300/50"
                    : "bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 border border-gray-200/50 hover:from-gray-100 hover:to-gray-200"
                )}
              >
                {method}
              </motion.button>
            ))}
          </div>
        );

      default: // text
        return (
          <input
            key={id}
            type="text"
            value={value}
            onChange={(e) => handlePlaceholderChange(id, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
            className={inputClassName}
          />
        );
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={cn(
        "border rounded-2xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.08)] backdrop-blur-sm transition-all duration-300",
        operation.approved && "border-green-300/50 bg-gradient-to-br from-green-50 to-green-100",
        operation.denied && "border-red-300/50 bg-gradient-to-br from-red-50 to-red-100",
        !operation.approved && !operation.denied && operation.type === 'editSetting' && "border-amber-200/50 bg-gradient-to-br from-amber-50 to-amber-100 hover:shadow-[0_8px_30px_rgb(245,158,11,0.15)]",
        !operation.approved && !operation.denied && operation.type !== 'editSetting' && "border-gray-200/50 bg-white/80 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
      )}
    >
      <div className="flex items-start space-x-3">
        <motion.div 
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="flex-shrink-0 mt-0.5"
        >
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-inner">
            {getOperationIcon(operation)}
          </div>
        </motion.div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <motion.h4 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="font-medium text-gray-900 text-sm tracking-tight"
            >
              {getOperationTitle(operation)}
            </motion.h4>
            {/* Category badge in top right */}
            {operation.data?.category_name && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="text-xs px-3 py-1 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 text-gray-600 font-medium ml-2 flex-shrink-0 shadow-inner"
              >
                {operation.data.category_name}
              </motion.span>
            )}
          </div>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="text-xs text-gray-600 mb-3 leading-relaxed"
          >
            {getOperationDescription(operation)}
          </motion.p>

          {/* Placeholder Input Fields */}
          {detectedPlaceholders.length > 0 && !operation.approved && !operation.denied && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="space-y-3 mb-4 p-4 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200/50 rounded-xl shadow-inner"
            >
              <div className="text-xs font-medium text-blue-800 mb-3 flex items-center space-x-2">
                <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-inner">
                  <PencilSimple className="w-2 h-2 text-blue-600" />
                </div>
                <span>Please fill in the following information:</span>
              </div>
              {detectedPlaceholders.map((placeholder, index) => (
                <motion.div 
                  key={placeholder.id} 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                  className="space-y-2"
                >
                  <label className="text-xs font-medium text-gray-700 flex items-center space-x-1">
                    <span>{placeholder.label}:</span>
                  </label>
                  <div className="relative">
                    {renderPlaceholderInput(placeholder)}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Complex Answer Placeholder Input Fields */}
          {operation.hasPlaceholders && operation.placeholders && !operation.approved && !operation.denied && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="space-y-3 mb-4 p-4 bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200/50 rounded-xl shadow-inner"
            >
              <div className="text-xs font-medium text-orange-800 mb-3 flex items-center space-x-2">
                <div className="w-4 h-4 rounded bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center shadow-inner">
                  <Warning className="w-2 h-2 text-orange-600" />
                </div>
                <span>Required Information:</span>
              </div>
              {operation.placeholders.map((placeholder, index) => (
                <motion.div 
                  key={`placeholder-${index}-${placeholder}`} 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                  className="space-y-2"
                >
                  <label className="text-xs font-medium text-gray-700 flex items-center space-x-1">
                    <span>{placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type={getPlaceholderFieldType(placeholder)}
                    value={complexAnswerEdits[placeholder] || ''}
                    onChange={(e) => handleComplexAnswerEdit(placeholder, e.target.value)}
                    placeholder={getPlaceholderFieldExample(placeholder)}
                    className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-300 bg-white/70 backdrop-blur-sm transition-all"
                    required
                  />
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* API Configuration Section */}
          {operation.isApiBasedAnswer && !operation.approved && !operation.denied && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="space-y-3 mb-4 p-4 bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200/50 rounded-xl shadow-inner"
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-purple-800 flex items-center space-x-2">
                  <div className="w-4 h-4 rounded bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center shadow-inner">
                    <Globe className="w-2 h-2 text-purple-600" />
                  </div>
                  <span>API Configuration:</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowApiConfig(!showApiConfig)}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center space-x-1"
                >
                  <span>{showApiConfig ? 'Hide' : 'Show'} Details</span>
                  <motion.div
                    animate={{ rotate: showApiConfig ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </motion.div>
                </motion.button>
              </div>

              {/* Basic API Config */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">
                    API Endpoint <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={complexAnswerEdits.api_endpoint || ''}
                    onChange={(e) => handleComplexAnswerEdit('api_endpoint', e.target.value)}
                    placeholder="https://api.example.com/v1/endpoint"
                    className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-300 bg-white/70 backdrop-blur-sm transition-all"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-700">HTTP Method</label>
                  <select
                    value={complexAnswerEdits.api_method || 'POST'}
                    onChange={(e) => handleComplexAnswerEdit('api_method', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-300 bg-white/70 backdrop-blur-sm transition-all"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                </div>
              </div>

              {/* Extended API Config */}
              <AnimatePresence>
                {showApiConfig && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="space-y-3"
                  >
                    {/* Authentication */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700">Authentication</label>
                        <select
                          value={complexAnswerEdits.auth_type || 'none'}
                          onChange={(e) => handleComplexAnswerEdit('auth_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-300 bg-white/70 backdrop-blur-sm transition-all"
                        >
                          <option value="none">No Authentication</option>
                          <option value="bearer">Bearer Token</option>
                          <option value="basic">Basic Auth</option>
                          <option value="api_key">API Key</option>
                        </select>
                      </div>
                      {complexAnswerEdits.auth_type === 'bearer' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-gray-700">
                            Bearer Token <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            value={complexAnswerEdits.auth_token || ''}
                            onChange={(e) => handleComplexAnswerEdit('auth_token', e.target.value)}
                            placeholder="your-bearer-token"
                            className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-300 bg-white/70 backdrop-blur-sm transition-all"
                          />
                        </div>
                      )}
                    </div>

                    {/* Request Body Template */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Request Body Template (JSON)</label>
                      <textarea
                        rows={3}
                        value={complexAnswerEdits.api_body_template || ''}
                        onChange={(e) => handleComplexAnswerEdit('api_body_template', e.target.value)}
                        placeholder='{"question_1": "{{question_1_response}}", "question_2": "{{question_2_response}}"}'
                        className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-300 font-mono bg-white/70 backdrop-blur-sm transition-all"
                      />
                      <p className="text-xs text-gray-500">
                        Use {`{{question_X_response}}`} to reference user answers
                      </p>
                    </div>

                    {/* Response Template */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Response Template</label>
                      <textarea
                        rows={2}
                        value={complexAnswerEdits.api_response_template || ''}
                        onChange={(e) => handleComplexAnswerEdit('api_response_template', e.target.value)}
                        placeholder="Your order status: {{response.status}}"
                        className="w-full px-3 py-2 border border-gray-200/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-300 font-mono bg-white/70 backdrop-blur-sm transition-all"
                      />
                      <p className="text-xs text-gray-500">
                        Use {`{{response.field}}`} to reference API response fields
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* View Details Button for Edit Operations */}
          {operation.type === 'editSetting' && !operation.approved && !operation.denied && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="mb-4"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowDetails(!showDetails)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1"
              >
                <span>{showDetails ? 'Hide Details' : 'View Details'}</span>
                <motion.div
                  animate={{ rotate: showDetails ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="mt-3 p-4 bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200/50 rounded-xl shadow-inner"
                  >
                    <div className="text-xs font-medium text-gray-700 mb-3 flex items-center space-x-2">
                      <div className="w-4 h-4 rounded bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-inner">
                        <PencilSimple className="w-2 h-2 text-gray-600" />
                      </div>
                      <span>Changes Preview:</span>
                    </div>

                    <div className="space-y-3">
                      {/* Show current vs new values */}
                      {Object.entries(operation.data).map(([key, newValue]) => {
                        if (key === 'id' || key === 'type' || key === 'setting_type' || key === 'name') return null;

                        let currentValue = currentSettingsData[operation.data.id]?.[key];
                        let displayNewValue = newValue;

                        // Handle special field mappings based on the key and setting type
                        const currentSetting = currentSettingsData[operation.data.id];
                        if (currentSetting) {
                          // Map backend fields to database fields
                          if (key === 'changes' && (currentSetting.type === 'simple_answer' || currentSetting.type === 'complex_answer')) {
                            // 'changes' field maps to 'content' in the database for answers
                            currentValue = currentSetting.content || currentSetting.answer;
                          } else if (key === 'content' || key === 'answer') {
                            // Direct content/answer field access
                            currentValue = currentSetting.content || currentSetting.answer || currentValue;
                          } else {
                            // Use the direct field mapping for other fields
                            currentValue = currentSetting[key];
                          }
                        }



                        // Parse JSON strings for better display and extract nested content
                        if (typeof newValue === 'string') {
                          try {
                            const parsed = JSON.parse(newValue);
                            if (typeof parsed === 'object') {
                              // If the parsed object has a content field, extract it
                              if (parsed.content !== undefined) {
                                displayNewValue = parsed.content;
                              } else {
                                displayNewValue = parsed;
                              }
                            }
                          } catch (e) {
                            // Keep as string if not valid JSON
                          }
                        } else if (typeof newValue === 'object' && newValue !== null) {
                          // If it's already an object, check for nested content
                          if (newValue.content !== undefined) {
                            displayNewValue = newValue.content;
                          } else {
                            displayNewValue = newValue;
                          }
                        }

                        const hasChange = currentValue !== newValue && newValue !== undefined && newValue !== null && newValue !== '';

                        if (!hasChange) return null;

                        return (
                          <div key={key} className="space-y-1">
                            <div className="space-y-3 text-xs">
                              {(() => {
                                const oldText = currentValue || '';
                                const newText = typeof displayNewValue === 'object' ? JSON.stringify(displayNewValue, null, 2) : (displayNewValue || '');
                                const diff = createDiff(oldText, newText);

                                if (diff.length === 0) {
                                  return (
                                    <div className="p-3 bg-gray-50 border border-gray-200/50 rounded-lg text-gray-600 text-xs text-center">
                                      No changes detected
                                    </div>
                                  );
                                }

                                return (
                                  <div className="space-y-1">
                                    <div className="font-medium text-gray-700 flex items-center space-x-2">
                                      <div className="flex items-center space-x-3 text-xs">
                                        <div className="flex items-center space-x-1">
                                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                          <span className="text-red-700">Removed</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                          <span className="text-green-700">Added</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200/50 rounded-lg font-mono text-xs overflow-hidden">
                                      {diff.map((change, index) => (
                                        <div
                                          key={index}
                                          className={cn(
                                            "px-3 py-1 whitespace-pre-wrap",
                                            change.type === 'removed' && "bg-red-50 text-red-800 border-l-2 border-red-400",
                                            change.type === 'added' && "bg-green-50 text-green-800 border-l-2 border-green-400",
                                            change.type === 'context' && "bg-gray-50 text-gray-600"
                                          )}
                                        >
                                          <span className="mr-2 select-none opacity-50">
                                            {change.type === 'removed' ? '-' : change.type === 'added' ? '+' : ' '}
                                          </span>
                                          {change.content}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Action Buttons */}
          {!operation.approved && !operation.denied && !operation.saving && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="flex space-x-3"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={
                  detectedPlaceholders.length > 0 || operation.hasPlaceholders || operation.isApiBasedAnswer
                    ? handleApproveWithPlaceholders 
                    : () => onApprove(operation)
                }
                disabled={!areAllPlaceholdersFilled()}
                className={cn(
                  "text-xs px-4 py-2 rounded-lg flex items-center space-x-2 transition-all font-medium shadow-inner",
                  !areAllPlaceholdersFilled()
                    ? "bg-gradient-to-br from-gray-100 to-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-br from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700"
                )}
              >
                <CheckCircle className="w-3 h-3" />
                <span>Approve</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onDeny(operation)}
                className="text-xs bg-gradient-to-br from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 flex items-center space-x-2 font-medium shadow-inner transition-all"
              >
                <XCircle className="w-3 h-3" />
                <span>Deny</span>
              </motion.button>
            </motion.div>
          )}

          {/* Status Indicators */}
          <AnimatePresence>
            {operation.saving && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center space-x-2 text-blue-600 mt-3"
              >
                <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center shadow-inner">
                  <CircleNotch className="w-2 h-2 animate-spin text-blue-600" />
                </div>
                <span className="text-xs font-medium">Saving...</span>
              </motion.div>
            )}

            {operation.saved && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center space-x-2 text-green-600 mt-3"
              >
                <div className="w-4 h-4 rounded bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center shadow-inner">
                  <CheckCircle className="w-2 h-2 text-green-600" />
                </div>
                <span className="text-xs font-medium">Saved Successfully</span>
              </motion.div>
            )}

            {operation.error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center space-x-2 text-red-600 mt-3"
              >
                <div className="w-4 h-4 rounded bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center shadow-inner">
                  <XCircle className="w-2 h-2 text-red-600" />
                </div>
                <span className="text-xs font-medium">Save Failed</span>
              </motion.div>
            )}

            {operation.denied && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex items-center space-x-2 text-red-600 mt-3"
              >
                <div className="w-4 h-4 rounded bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center shadow-inner">
                  <XCircle className="w-2 h-2 text-red-600" />
                </div>
                <span className="text-xs font-medium">Denied</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default BrainChatInterface; 